// ══════════════════════════════════════════════════════════════════════════════
// MATCHING PRO - Suggestions automatiques de pros/offres correspondants
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Star, BadgeCheck, MapPin, MessageCircle, X,
  ChevronRight, Users, Target, Zap, ThumbsUp, Send, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ══════════════════════════════════════════════════════════════
// COMPOSANT: Liste des pros matchants pour une offre
// ══════════════════════════════════════════════════════════════

export function MatchingProsPanel({ postId, isOpen, onClose }) {
  const [matches, setMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (isOpen && postId) {
      loadMatches();
    }
  }, [isOpen, postId]);

  const loadMatches = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('client_token');
      const response = await fetch(`${API_URL}/api/reseau-pro/matching/${postId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setMatches(data.matches || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error loading matches:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContact = (pro) => {
    toast.success(`Message envoyé à ${pro.name}`);
    // TODO: Ouvrir la messagerie avec ce pro
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[80vh]"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">Pros correspondants</h2>
                  <p className="text-sm text-white/70">{total} profils trouvés</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg text-white">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : matches.length === 0 ? (
              <div className="text-center py-12">
                <Users size={48} className="text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-700 mb-2">Aucun match trouvé</h3>
                <p className="text-gray-500 text-sm">Essaie d'élargir tes critères</p>
              </div>
            ) : (
              <div className="space-y-3">
                {matches.map((pro, index) => (
                  <motion.div
                    key={pro.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] flex items-center justify-center text-white font-bold">
                          {pro.avatar}
                        </div>
                        {/* Score badge */}
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#F97316] flex items-center justify-center text-white text-xs font-bold">
                          {pro.match_score}%
                        </div>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 truncate">{pro.name}</span>
                          {pro.verified && <BadgeCheck size={14} className="text-[#1E3A5F]" />}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                          <span>{pro.metier}</span>
                          {pro.location && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <MapPin size={12} />
                                {pro.location}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          {pro.rating > 0 && (
                            <div className="flex items-center gap-1 text-sm">
                              <Star size={12} className="text-amber-500 fill-amber-500" />
                              <span className="font-medium">{pro.rating}</span>
                              {pro.reviews_count > 0 && (
                                <span className="text-gray-400">({pro.reviews_count})</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Match reasons */}
                        {pro.match_reasons?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {pro.match_reasons.slice(0, 3).map((reason, i) => (
                              <span key={i} className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                ✓ {reason}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action */}
                      <Button
                        size="sm"
                        onClick={() => handleContact(pro)}
                        className="bg-[#F97316] hover:bg-[#ea6c0a] text-white flex-shrink-0"
                      >
                        <MessageCircle size={14} className="mr-1" />
                        Contacter
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {matches.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Les scores sont calculés automatiquement selon le métier, la localisation et les avis
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ══════════════════════════════════════════════════════════════
// COMPOSANT: Widget "Offres pour toi" dans le feed
// ══════════════════════════════════════════════════════════════

export function MatchingOffersWidget() {
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    try {
      const token = localStorage.getItem('client_token');
      const response = await fetch(`${API_URL}/api/reseau-pro/matching-for-me?limit=5`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setOffers(data.offers || []);
      }
    } catch (error) {
      console.error('Error loading matching offers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return null;
  if (offers.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] rounded-2xl p-5 text-white mb-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Target size={20} />
          </div>
          <div>
            <h3 className="font-semibold">Offres pour toi</h3>
            <p className="text-sm text-white/70">{offers.length} opportunités matchent ton profil</p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-2 hover:bg-white/20 rounded-lg transition-colors"
        >
          <ChevronRight size={20} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </button>
      </div>

      {/* Preview (première offre) */}
      {!isExpanded && offers.length > 0 && (
        <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F97316] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {offers[0].match_score}%
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm line-clamp-2">{offers[0].content?.slice(0, 100)}...</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-white/70">{offers[0].author?.name}</span>
                <span className="text-white/50">•</span>
                <span className="text-xs text-white/70">{offers[0].time}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste complète */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="space-y-2 overflow-hidden"
          >
            {offers.map((offer, index) => (
              <motion.div
                key={offer.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/10 rounded-xl p-3 backdrop-blur-sm hover:bg-white/20 transition-colors cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F97316] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {offer.match_score}%
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm line-clamp-2">{offer.content?.slice(0, 80)}...</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-white/70">{offer.author?.name}</span>
                      {offer.location && (
                        <>
                          <span className="text-white/50">•</span>
                          <span className="text-xs text-white/70 flex items-center gap-1">
                            <MapPin size={10} />
                            {offer.location}
                          </span>
                        </>
                      )}
                    </div>
                    {/* Match reasons */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {offer.match_reasons?.slice(0, 2).map((reason, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/20 text-white text-xs rounded-full">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      {isExpanded && (
        <div className="mt-4 text-center">
          <Button
            size="sm"
            variant="outline"
            className="border-white/30 text-white hover:bg-white/20 bg-transparent"
          >
            <Eye size={14} className="mr-2" />
            Voir toutes les opportunités
          </Button>
        </div>
      )}
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════
// COMPOSANT: Bouton "Voir les pros correspondants" pour un post
// ══════════════════════════════════════════════════════════════

export function MatchingButton({ postId, postType, className = "" }) {
  const [showPanel, setShowPanel] = useState(false);

  // Ne montrer que pour les posts de sous-traitance, partenariat, opportunité
  const showableTypes = ['sous_traitance', 'partenariat', 'opportunite'];
  if (!showableTypes.includes(postType)) return null;

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowPanel(true)}
        className={`border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/10 ${className}`}
      >
        <Sparkles size={14} className="mr-1" />
        Voir les pros ({postType === 'sous_traitance' ? 'sous-traitants' : 'partenaires'})
      </Button>

      <MatchingProsPanel
        postId={postId}
        isOpen={showPanel}
        onClose={() => setShowPanel(false)}
      />
    </>
  );
}

export default { MatchingProsPanel, MatchingOffersWidget, MatchingButton };
